import api from "@/lib/axiosConfig";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

const baseRoute = "back/api/tirage-mouvements";

const _tirageMouvementSchema = z.object({
  id: z.string(),
  tirageId: z.string(),
  instanceId: z.string(),
  description: z.string(),
  lieu: z.string(),
  dateDepart: z.string(),
  dateRetour: z.string(),
  constatEtatDepart: z.boolean(),
  constatEtatRetour: z.boolean(),
  notes: z.string(),
  sequence: z.number(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});

export type TirageMouvement = z.infer<typeof _tirageMouvementSchema>;

const _createTirageMouvementSchema = _tirageMouvementSchema.omit({
  id: true,
  instanceId: true,
  sequence: true,
  createdAt: true,
  updatedAt: true,
});

export type CreateTirageMouvement = z.infer<typeof _createTirageMouvementSchema>;

export type EditTirageMouvement = Omit<CreateTirageMouvement, "tirageId">;

const mouvementsQueryKey = (tirageId: string | undefined) => [
  "tirage-mouvements",
  tirageId,
];

async function getTirageMouvements(tirageId: string) {
  const { data } = await api.get<TirageMouvement[]>(baseRoute, {
    params: { tirageId },
  });
  return data;
}

export function useGetTirageMouvements(tirageId: string | undefined) {
  return useQuery({
    queryKey: mouvementsQueryKey(tirageId),
    queryFn: () =>
      tirageId ? getTirageMouvements(tirageId) : Promise.resolve([]),
    enabled: !!tirageId,
  });
}

async function createTirageMouvement(params: CreateTirageMouvement) {
  const { data } = await api.post<TirageMouvement>(baseRoute, params);
  return data;
}

export function useCreateTirageMouvement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createTirageMouvement,
    onSuccess: (_, { tirageId }) => {
      queryClient.invalidateQueries({
        queryKey: mouvementsQueryKey(tirageId),
      });
    },
  });
}

async function editTirageMouvement({
  mouvementId,
  mouvement,
}: {
  mouvementId: string;
  tirageId: string;
  mouvement: EditTirageMouvement;
}) {
  const { data } = await api.put<TirageMouvement>(
    `${baseRoute}/${mouvementId}`,
    mouvement,
  );
  return data;
}

export function useEditTirageMouvement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: editTirageMouvement,
    onSuccess: (_, { tirageId }) => {
      queryClient.invalidateQueries({
        queryKey: mouvementsQueryKey(tirageId),
      });
    },
  });
}

async function deleteTirageMouvement({
  mouvementId,
}: {
  mouvementId: string;
  tirageId: string;
}) {
  await api.delete(`${baseRoute}/${mouvementId}`);
}

export function useDeleteTirageMouvement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteTirageMouvement,
    onSuccess: (_, { tirageId }) => {
      queryClient.invalidateQueries({
        queryKey: mouvementsQueryKey(tirageId),
      });
    },
  });
}

async function duplicateTirageMouvement({
  mouvementId,
}: {
  mouvementId: string;
  tirageId: string;
}) {
  const { data } = await api.post<TirageMouvement>(
    `${baseRoute}/${mouvementId}/duplicate`,
  );
  return data;
}

export function useDuplicateTirageMouvement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: duplicateTirageMouvement,
    onSuccess: (_, { tirageId }) => {
      queryClient.invalidateQueries({
        queryKey: mouvementsQueryKey(tirageId),
      });
    },
  });
}

async function updateTirageMouvementSequence({
  newSequence,
  tirageId,
}: {
  newSequence: string[];
  tirageId: string;
}) {
  await api.put(`${baseRoute}/sequence`, {
    tirageId,
    sequence: newSequence,
  });
}

export function useUpdateTirageMouvementSequence() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateTirageMouvementSequence,
    onMutate: async ({ newSequence, tirageId }) => {
      const key = mouvementsQueryKey(tirageId);
      await queryClient.cancelQueries({ queryKey: key });

      const previous = queryClient.getQueryData<TirageMouvement[]>(key);

      if (previous) {
        const byId = new Map(previous.map((m) => [m.id, m]));
        const reordered = newSequence
          .map((id, index) => {
            const m = byId.get(id);
            return m ? { ...m, sequence: index } : undefined;
          })
          .filter((m): m is TirageMouvement => m !== undefined);
        queryClient.setQueryData(key, reordered);
      }

      return { previous };
    },
    onError: (_err, { tirageId }, context) => {
      if (context?.previous) {
        queryClient.setQueryData(mouvementsQueryKey(tirageId), context.previous);
      }
    },
  });
}
